import { supabase } from "./supabase";

export interface AuthToken {
  access_token: string;
  refresh_token?: string;
  expires_at?: number;
}

/**
 * Get the current access token from Supabase Auth
 */
export async function getAccessToken(): Promise<string | null> {
  try {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();

    if (error) {
      console.error("Error getting session:", error);
      return null;
    }

    // Session data retrieved successfully

    return session?.access_token || null;
  } catch (error) {
    console.error("Error getting access token:", error);
    return null;
  }
}

/**
 * Refresh the access token if needed
 */
export async function refreshAccessToken(): Promise<string | null> {
  try {
    const {
      data: { session },
      error,
    } = await supabase.auth.refreshSession();

    if (error) {
      console.error("Error refreshing session:", error);
      return null;
    }

    return session?.access_token || null;
  } catch (error) {
    console.error("Error refreshing access token:", error);
    return null;
  }
}

/**
 * Create Authorization header with Bearer token
 */
export async function createAuthHeaders(): Promise<Record<string, string>> {
  const token = await getAccessToken();

  if (token) {
    // Creating auth headers with valid token

    return {
      Authorization: `Bearer ${token}`,
    };
  }

  console.warn("No token available for auth headers");
  return {};
}

/**
 * Check if the current session is valid
 */
export async function isSessionValid(): Promise<boolean> {
  try {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();

    if (error || !session) {
      return false;
    }

    if (isTokenExpired(session.access_token)) {
      const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError || !refreshData.session) {
        return false;
      }
      return true;
    }

    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Check if a JWT token is expired
 */
function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  } catch (error) {
    return true;
  }
}

/**
 * Get token expiration time in milliseconds
 */
export function getTokenExpirationTime(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp * 1000; // Convert to milliseconds
  } catch (error) {
    console.error("Error parsing token expiration:", error);
    return null;
  }
}

/**
 * Get time until token expires in milliseconds
 */
export function getTimeUntilExpiration(token: string): number | null {
  const expirationTime = getTokenExpirationTime(token);
  if (!expirationTime) return null;

  const currentTime = Date.now();
  return Math.max(0, expirationTime - currentTime);
}

/**
 * Get user information from the JWT token
 */
export function getUserFromToken(token: string) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return {
      id: payload.sub,
      email: payload.email,
      role: payload.role,
      aud: payload.aud,
      exp: payload.exp,
      iat: payload.iat,
    };
  } catch (error) {
    console.error("Error parsing token:", error);
    return null;
  }
}
